import React, { useState } from 'react';
import styled from 'styled-components';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Cell,
  ReferenceLine
} from 'recharts';

// Custom tooltip to explain each factor
function CustomTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    const data = payload[0].payload;

    const descriptions = {
      'Base Quality': 'Points from comment/reply quality and depth',
      'Length Bonus': 'Bonus for substantial content (avg length)',
      'Coverage': 'Points for engaging with multiple questions',
      'Community': 'Bonus for replying to other users',
      'Time': 'Points for time spent in session',
      'Visits': 'Points for return visits'
    };

    return (
      <TooltipContainer>
        <TooltipLabel>{data.name}</TooltipLabel>
        <TooltipValue>+{data.value} points</TooltipValue>
        {descriptions[data.name] && (
          <TooltipDescription>{descriptions[data.name]}</TooltipDescription>
        )}
        <TooltipCumulative>Running Total: {data.cumulative}</TooltipCumulative>
      </TooltipContainer>
    );
  }
  return null;
}

function ContentQualityWaterfallChart({ users }) {
  const [selectedUserId, setSelectedUserId] = useState(
    users && users.length > 0 ? users[0].uid : null
  );

  if (!users || users.length === 0) {
    return (
      <EmptyState>
        <EmptyMessage>No user data available</EmptyMessage>
      </EmptyState>
    );
  }

  const selectedUser = users.find((u) => u.uid === selectedUserId);
  const breakdown = selectedUser?.qualityBreakdown;

  // Build waterfall data structure only if breakdown exists
  const waterfallData = [];
  if (breakdown) {
    let cumulative = 0;

    // Helper to add waterfall step
    const addStep = (name, value, color) => {
      if (value > 0) {
        const start = cumulative;
        cumulative += value;
        waterfallData.push({
          name,
          value,
          start,
          cumulative,
          color
        });
      }
    };

    // Add each component in order
    addStep('Base Quality', breakdown.baseContentScore, '#3B82F6'); // Blue
    addStep('Length Bonus', breakdown.avgLengthBonus, '#10B981'); // Green
    addStep('Coverage', breakdown.coverageScore, '#8B5CF6'); // Purple
    addStep('Community', breakdown.communityBonus, '#F59E0B'); // Orange
    addStep('Time', breakdown.timeScore, '#EC4899'); // Pink
    addStep('Visits', breakdown.visitScore, '#14B8A6'); // Teal

    // Note: No adjustments are applied since all users have equal opportunity
    // to engage with all questions via email notifications
  }

  return (
    <Container>
      <Controls>
        <Label>Analyze User:</Label>
        <Select
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}>
          {users.map((user) => (
            <option key={user.uid} value={user.uid}>
              {user.name} (Score: {user.engagementScore})
            </option>
          ))}
        </Select>
      </Controls>

      {!breakdown ? (
        <EmptyState>
          <EmptyMessage>
            Quality breakdown not available for this user. Try selecting a different user.
          </EmptyMessage>
        </EmptyState>
      ) : (
        <>
          <UserInfo>
        <InfoRow>
          <InfoLabel>Total Comments:</InfoLabel>
          <InfoValue>
            {breakdown.contentQualityMetrics?.commentCount || 0}
          </InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Avg. Comment Length:</InfoLabel>
          <InfoValue>
            {breakdown.contentQualityMetrics?.averageLength || 0} chars
          </InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Total Content:</InfoLabel>
          <InfoValue>
            {breakdown.contentQualityMetrics?.totalContentLength || 0} chars
          </InfoValue>
        </InfoRow>
      </UserInfo>

      <ChartWrapper>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart
            data={waterfallData}
            margin={{
              top: 20,
              right: 90,
              left: 20,
              bottom: 60
            }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12, fill: '#666', dy: 5 }}
              angle={-25}
              textAnchor="end"
              height={80}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 12, fill: '#666' }}
              label={{
                value: 'Engagement Score',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fill: '#666', fontSize: '12px' }
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={breakdown.finalScore}
              stroke="#EF4444"
              strokeDasharray="5 5"
              strokeWidth={2}
              label={{
                value: `Final: ${breakdown.finalScore}`,
                position: 'right',
                fill: '#EF4444',
                fontSize: 12,
                fontWeight: 'bold'
              }}
            />

            {/* Invisible bars for positioning */}
            <Bar dataKey="start" stackId="a" fill="transparent" />

            {/* Actual value bars with colors */}
            <Bar dataKey="value" stackId="a" radius={[4, 4, 0, 0]}>
              {waterfallData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </ChartWrapper>

      <Legend>
        <LegendTitle>Score Breakdown:</LegendTitle>
        <LegendGrid>
          {waterfallData.map((item) => (
            <LegendItem key={item.name}>
              <LegendColor $color={item.color} />
              <LegendText>
                {item.name}: <strong>+{item.value.toFixed(1)}</strong>
              </LegendText>
            </LegendItem>
          ))}
        </LegendGrid>
        <FinalScore>
          Final Engagement Score: <strong>{breakdown.finalScore}</strong> / 100
        </FinalScore>
      </Legend>
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  padding: 1rem;
  width: 100%;
`;

const Controls = styled.div`
  align-items: center;
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Label = styled.label`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;

  @media (max-width: 767px) {
    font-size: 0.9rem;
  }
`;

const Select = styled.select`
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  color: var(--brown);
  cursor: pointer;
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  max-width: 400px;
  padding: 0.65rem 1rem;

  &:focus {
    border-color: var(--blue);
    outline: none;
  }

  @media (max-width: 767px) {
    max-width: 100%;
  }
`;

const UserInfo = styled.div`
  background: #f9f9f9;
  border-radius: 6px;
  display: flex;
  gap: 2rem;
  margin-bottom: 1.5rem;
  padding: 1rem 1.5rem;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const InfoRow = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const InfoLabel = styled.span`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
`;

const InfoValue = styled.span`
  color: var(--brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
`;

const ChartWrapper = styled.div`
  width: 100%;
`;

const Legend = styled.div`
  margin-top: 1.5rem;
`;

const LegendTitle = styled.h4`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
`;

const LegendGrid = styled.div`
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  margin-bottom: 1rem;
`;

const LegendItem = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;
`;

const LegendColor = styled.div`
  background-color: ${({ $color }) => $color};
  border-radius: 3px;
  height: 12px;
  width: 12px;
`;

const LegendText = styled.span`
  color: var(--brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;

  strong {
    color: var(--brown);
  }
`;

const FinalScore = styled.div`
  background: #f0f9ff;
  border-left: 4px solid var(--blue);
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  margin-top: 1rem;
  padding: 1rem 1.5rem;

  strong {
    color: var(--blue);
    font-size: 1.2rem;
  }
`;

const EmptyState = styled.div`
  align-items: center;
  background: #f9f9f9;
  border-radius: 8px;
  display: flex;
  height: 300px;
  justify-content: center;
`;

const EmptyMessage = styled.p`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  margin: 0;
`;

const TooltipContainer = styled.div`
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 0.75rem;
`;

const TooltipLabel = styled.div`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const TooltipValue = styled.div`
  color: var(--blue);
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const TooltipCumulative = styled.div`
  border-top: 1px solid #e5e5e5;
  color: var(--brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
`;

const TooltipDescription = styled.div`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  font-style: italic;
  margin-bottom: 0.5rem;
`;

export default ContentQualityWaterfallChart;
